<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { isPlaceholderEmail, placeholderEmailToPhone } from '@lib/utils/phoneToPlaceholderEmail';
import { Button } from '@/components/ui/button'
import { AutoForm } from '@/ui-plus/auto-form'
import { toast } from '@/ui-plus/sonner'
import { useFormPlus } from "@/ui-plus/form/useFormPlus";
import { toTypedSchema } from "@vee-validate/zod";
import { authGlobalApiClient } from '@api-client/auth_global';
import { useForm } from "vee-validate";
import { z } from "zod";
import { Loader2 } from 'lucide-vue-next';
import { authState } from '@/ui-plus/useAuth';
import { useI18n } from "vue-i18n";
const { t } = useI18n();

const emit = defineEmits<{
    onSuccess: [values: z.infer<typeof schema>],
    onCancel: [],
    onError: [error: any],
}>();

const schema = z.object({
    password: z.string()
        .min(8, { message: "min 8, use only StroNg_P@ssw0rd" })
        .max(255, { message: "max 255" }),
    confirm: z.string(),
}).refine(data => data.password === data.confirm, {
    message: t('passwordsMustMatch'),
    path: ['confirm'],
})

const form = useForm({
    validationSchema: toTypedSchema(schema)
});
const { isLoading, globalError, handleError, withSubmit } = useFormPlus(form);

const username = ref("");
onMounted(() => {
    const email = authState.value.accessTokenPayload?.email;
    if (!email) {
        return;
    }
    if (isPlaceholderEmail(email)) {
        username.value = placeholderEmailToPhone(email);
    } else {
        username.value = email;
    }
});

async function onSubmit(values: z.infer<typeof schema>) {
    await withSubmit(async () => {
        const { error } = await (await authGlobalApiClient.user['update-password'].$post({
            form: {
                password: values.password,
            },
        })).json()
        if (error) {
            handleError(error, form);
        } else {
            toast.success(t('passwordUpdated'));
            emit("onSuccess", values);
        }
    })
}
</script>

<template>

    <AutoForm class="space-y-6"
              :schema="schema"
              :form="form"
              @submit="onSubmit"
              :field-config="{
                password: {
                    label: t('newPassword'),
                    inputProps: {
                        type: 'password',
                        autofocus: true,
                        autocomplete: 'new-password',
                    },
                },
                confirm: {
                    label: t('confirmPassword'),
                    inputProps: {
                        type: 'password',
                        autocomplete: 'new-password',
                    },
                },
            }">
        <div class="text-sm font-medium text-destructive"
             v-if="globalError">
            {{ globalError }}
        </div>

        <input type="text"
               :value="username"
               name="username"
               autocomplete="username"
               readonly
               tabindex="-1"
               aria-hidden="true"
               class="sr-only" />

        <div>
            <Button type="submit"
                    :disabled="isLoading"
                    class="w-full my-6">
                <Loader2 v-if="isLoading"
                         class="animate-spin" />
                {{ t('update') }}
            </Button>
        </div>
    </AutoForm>

    <Button variant="secondary"
            @click="emit('onCancel')"> {{ t('cancel') }} </Button>
</template>
